import { useEffect, useRef, useState } from "react";

/**
 * Composer draft state (step 6.1).
 *
 * Owns the pure per-render draft state of the message composer: the draft
 * `text` (+ the textarea auto-resize effect and its `inputRef`), the `spoiler`
 * toggle, the inline message-edit buffer (`editingId`/`editText`), the current
 * `replyTo` target, and the quick-reaction `favEmojis` list (+ its localStorage
 * load and the `favEmojis:updated`/storage listeners).
 *
 * The send/edit/reply *logic* (send, onInputChange, insertEmojiAtCaret,
 * startEdit/saveEdit/cancelEdit, handleCancelReply) stays in the composition
 * layer and reads these values — it belongs with the message cluster (6.3).
 * The edit/reply setters are returned so the message list can drive them
 * (the "narrow interface": MessageList already takes setReplyTo/setEditText/
 * editingId/editText as props).
 *
 * Generic over the message type `TMsg` so `replyTo` keeps the parent's full
 * message shape without back-importing it (rule 5; the useDms precedent).
 */
export function useComposer<TMsg>() {
  // Draft text + the composer textarea.
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Auto-grow the textarea to fit its content (capped at 220px).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(220, el.scrollHeight) + 'px';
  }, [text]);

  const [spoiler, setSpoiler] = useState<boolean>(false);

  // message editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  const [replyTo, setReplyTo] = useState<TMsg | null>(null);

  // Favorite emojis from Personalization
  const defaultFavEmojis = ['😀','😂','😍','👍','🎉','🔥'];
  function normalizeFavEmojis(arr: unknown): string[] | null {
    if (!Array.isArray(arr)) return null;
    const uniq: string[] = [];
    for (const e of arr) {
      if (typeof e !== 'string') continue;
      if (!uniq.includes(e)) uniq.push(e);
      if (uniq.length >= 6) break;
    }
    if (uniq.length < 6) {
      for (const d of defaultFavEmojis) {
        if (uniq.length >= 6) break;
        if (!uniq.includes(d)) uniq.push(d);
      }
    }
    return uniq.length ? uniq.slice(0, 6) : null;
  }
  function getFavEmojis(): string[] {
    try {
      const raw = localStorage.getItem('favEmojis');
      const arr = raw ? JSON.parse(raw) : null;
      const norm = normalizeFavEmojis(arr) || defaultFavEmojis;
      const normJson = JSON.stringify(norm);
      if (raw !== normJson) {
        localStorage.setItem('favEmojis', normJson);
      }
      return norm;
    } catch { /* noop */ }
    return defaultFavEmojis;
  }
  const [favEmojis, setFavEmojis] = useState<string[]>(getFavEmojis());
  useEffect(() => {
    const refresh = () => { setFavEmojis(getFavEmojis()); };
    // Custom event fired by settings when favorites change
    window.addEventListener('favEmojis:updated', refresh as EventListener);
    // Cross-tab/local changes
    const onStorage = (e: StorageEvent) => { if (e.key === 'favEmojis') refresh(); };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('favEmojis:updated', refresh as EventListener);
      window.removeEventListener('storage', onStorage);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- subscribes once; getFavEmojis only reads localStorage
  }, []);

  return {
    text,
    setText,
    inputRef,
    spoiler,
    setSpoiler,
    editingId,
    setEditingId,
    editText,
    setEditText,
    replyTo,
    setReplyTo,
    favEmojis,
    setFavEmojis,
  };
}
